import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { Button, Card, CardBody, CardHeader, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, useDisclosure } from "@nextui-org/react";
import ControlledInput from "../../../../elements/ControlledInput";
import toast from "react-hot-toast";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { isResponseErrorObject } from "../../../../../services/helpers";
import useSWR from "swr";
import { getFetcher, postFetcher } from "../../../../../services/api";
import ControlledReactSelect from "../../../../elements/ControlledReactSelect";
import DataPICSelector from "../ModalDataPIC";
import useListPic from "../../../../../hooks/useListPic";
import { yupResolver } from "@hookform/resolvers/yup";
import { inlineSupplierValidation } from "../../../../../services/validation/supplier.validation";

export default function InlineEditSupplier({ selectedCompany, control, setValue, isDisabled = false }) {
  const purchaseSum = useWatch({ control, name: "dataSuppliers" });
  const { isOpen: isOpenModalForm, onOpen: onOpenModalForm, onOpenChange: onOpenChangeModalForm, onClose: onCloseModalForm } = useDisclosure();
  const { fields, append, remove } = useFieldArray({ control, name: "dataSuppliers" });
  const {
    register: registerInline,
    control: controlInline,
    handleSubmit: handleSubmitInline,
    reset: resetInline,
    watch: watchInline,
    formState: { isSubmitting: isSubmittingInline, errors: errorsInline },
  } = useForm({ resolver: yupResolver(inlineSupplierValidation) });
  const { data: dataSupllier, isLoading: isLoadingSupplier, mutate: mutateCustomer } = useSWR(selectedCompany ? `/api/company/data-suppliers/${selectedCompany}` : null, getFetcher);
  const provinceId = watchInline("provinceId");
  const regencyId = watchInline("regencyId");
  const districtId = watchInline("districtId");
  const companyId = watchInline("companyId");

  const { data: province } = useSWR("/api/location/provinces", getFetcher);
  const { data: regency } = useSWR(provinceId ? `/api/location/cities?provinceId=${provinceId}` : null, getFetcher);
  const { data: district } = useSWR(regencyId ? `/api/location/districts?regencyId=${regencyId}` : null, getFetcher);
  const { data: village } = useSWR(districtId ? `/api/location/villages?districtId=${districtId}` : null, getFetcher);
  const { dataPIC, isLoadingDataPIC, mutatePIC } = useListPic({ companyId: selectedCompany });

  function onCloseForm() {
    resetInline({
      companyId: "",
      namaSupplier: "",
      alamat: "",
      email: "",
      telepon: "",
      fax: "",
      latitude: "",
      longitude: "",
      provinceId: "",
      regencyId: "",
      districtId: "",
      villageId: "",
      dataPICIds: [],
    });
    onCloseModalForm();
  }

  async function onSubmitForm(formData) {
    try {
      const response = await postFetcher(`/api/company/data-supplier/${selectedCompany}`, formData);
      mutateCustomer();
      append({ supplierId: response.id, jumlahPembelianB3: 0 });
      toast.success("Perusahaan supplier berhasil ditambah!");
      onCloseForm();
    } catch (error) {
      isResponseErrorObject(error.response.data.message)
        ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            toast.error(value);
          })
        : toast.error(error.response.data.message);
    }
  }

  const totalPurchase = Array.isArray(purchaseSum)
    ? purchaseSum.reduce((total, field) => {
        const value = parseFloat(field.jumlahPembelianB3) || 0;
        // Bulatkan agar galat floating point (0.1 + 0.2 = 0.30000000000000004) tidak ikut tampil/tersimpan
        const sum = Number((total + value).toFixed(6));
        setValue("jumlahPembelianB3", sum);
        return sum;
      }, 0)
    : 0;

  return (
    <>
      <>
        <Card radius="sm">
          <CardHeader className="w-full flex justify-between">
            <div className="text-sm">
              Perusahaan Supplier <span className="text-danger ml-0.5">*</span>
            </div>
            <Button isDisabled={isDisabled} onPress={onOpenModalForm} color="primary" startContent={<PlusIcon className="size-4" />}>
              Tambah Perusahaan Supplier
            </Button>
          </CardHeader>
          <CardBody>
            <div className="flex items-center gap-3 mb-3">
              <Button size="sm" isDisabled={isDisabled} onPress={() => append({ supplierId: "", jumlahPembelianB3: 0 })} color="primary" startContent={<PlusIcon className="size-4" />}>
                Tambah Pembelian
              </Button>
              <span className="text-sm">
                Total Pembelian: <strong>{totalPurchase}</strong>
              </span>
            </div>
            <Table removeWrapper aria-label="table-customer">
              <TableHeader>
                <TableColumn>Nama</TableColumn>
                <TableColumn>Jumlah Pembelian (KG)</TableColumn>
                <TableColumn>Aksi</TableColumn>
              </TableHeader>
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <ControlledReactSelect label="" name={`dataSuppliers[${index}].supplierId`} control={control} options={dataSupllier?.map((item) => ({ value: item.id, label: item.namaSupplier }))} isLoading={isLoadingSupplier} />
                    </TableCell>
                    <TableCell>
                      <ControlledInput type="number" label="" name={`dataSuppliers[${index}].jumlahPembelianB3`} isRequired={true} control={control} />
                    </TableCell>
                    <TableCell>
                      <Button color="danger" size="sm" isIconOnly onClick={() => remove(index)} isDisabled={fields.length === 1}>
                        <TrashIcon className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      </>

      <Modal scrollBehavior="inside" size="3xl" isOpen={isOpenModalForm} onOpenChange={onOpenChangeModalForm} onClose={onCloseForm} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Tambah Perusahaan Supplier</ModalHeader>
              <ModalBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  <ControlledInput label="Supplier" name="namaSupplier" isRequired={true} control={controlInline} />
                  <ControlledInput label="Alamat" name="alamat" isRequired={true} control={controlInline} />
                  <ControlledInput label="Email" name="email" isRequired={true} control={controlInline} />
                  <ControlledInput label="Telepon" name="telepon" isRequired={true} control={controlInline} />
                  <ControlledInput label="Fax" name="fax" isRequired={true} control={controlInline} />
                  <ControlledInput label="Latitude" name="latitude" isRequired={true} control={controlInline} />
                  <ControlledInput label="Longitude" name="longitude" isRequired={true} control={controlInline} />
                  <Select
                    {...registerInline("provinceId")}
                    isRequired
                    variant="faded"
                    label="Provinsi"
                    labelPlacement="outside"
                    placeholder="Pilih..."
                    color={errorsInline.provinceId ? "danger" : "default"}
                    isInvalid={errorsInline.provinceId}
                    errorMessage={errorsInline.provinceId && errorsInline.provinceId.message}
                  >
                    {province?.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select
                    {...registerInline("regencyId")}
                    isRequired
                    variant="faded"
                    label="Kabupaten"
                    labelPlacement="outside"
                    placeholder="Pilih..."
                    color={errorsInline.regencyId ? "danger" : "default"}
                    isInvalid={errorsInline.regencyId}
                    errorMessage={errorsInline.regencyId && errorsInline.regencyId.message}
                  >
                    {regency?.map((item) => (
                      <SelectItem key={item.id}>{item.name}</SelectItem>
                    ))}
                  </Select>
                  <Select
                    {...registerInline("districtId")}
                    isRequired
                    variant="faded"
                    label="Kecamatan"
                    labelPlacement="outside"
                    placeholder="Pilih..."
                    color={errorsInline.districtId ? "danger" : "default"}
                    isInvalid={errorsInline.districtId}
                    errorMessage={errorsInline.districtId && errorsInline.districtId.message}
                  >
                    {district?.map((item) => (
                      <SelectItem key={item.id}>{item.name}</SelectItem>
                    ))}
                  </Select>
                  <Select
                    {...registerInline("villageId")}
                    isRequired
                    variant="faded"
                    label="Kelurahan"
                    labelPlacement="outside"
                    placeholder="Pilih..."
                    color={errorsInline.villageId ? "danger" : "default"}
                    isInvalid={errorsInline.villageId}
                    errorMessage={errorsInline.villageId && errorsInline.villageId.message}
                  >
                    {village?.map((item) => (
                      <SelectItem key={item.id}>{item.name}</SelectItem>
                    ))}
                  </Select>
                  <div className="col-span-2">
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <ControlledReactSelect control={controlInline} label="Data PIC" name="dataPICIds" isLoading={isLoadingDataPIC} options={dataPIC?.map((item) => ({ value: item.id, label: item.namaPIC }))} isMulti={true} />
                      </div>
                      <DataPICSelector type={"supplier"} mutatePIC={mutatePIC} selectedCompany={selectedCompany} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button onPress={handleSubmitInline(onSubmitForm)} isLoading={isSubmittingInline} isDisabled={isSubmittingInline} type="submit" color="primary">
                    Tambah
                  </Button>
                  <Button isDisabled={isSubmittingInline} color="danger" variant="faded" onPress={onClose}>
                    Batal
                  </Button>
                </div>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
